import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { detectTimeZone, getTimeZoneOptions } from '../utils/timezones';

export default function register({ onRegister }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordcheck, setPasswordCheck] = useState('');
  const [timezone, setTimezone] = useState(detectTimeZone());
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const showError = (message) => {
    setSuccess('');
    setError(message);
    setTimeout(() => setError(''), 5000);
  };

  const showSuccess = (message) => {
    setError('');
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const validatePassword = (password) => {
    const hasCapitalLetter = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    return hasCapitalLetter && hasSpecialChar && hasNumber;
  };

  const timeZoneOptions = getTimeZoneOptions();

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      backgroundColor: 'var(--surface)',
      borderColor: state.isFocused ? 'var(--brand)' : 'var(--border)',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.18)' : 'none',
      borderRadius: '10px',
      color: 'var(--panel-text)',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--panel-text)',
    }),
    input: (provided) => ({
      ...provided,
      color: 'var(--panel-text)',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--panel-muted)',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--surface-strong)',
      borderRadius: '10px',
      overflow: 'hidden',
      zIndex: 20,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '220px',
      backgroundColor: 'var(--surface-strong)',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'rgba(102, 126, 234, 0.12)' : 'transparent',
      color: 'var(--panel-text)',
    }),
  };

  const registeraccount = async () => {
    if (!username || !password || !passwordcheck) {
      showError('Fill in all fields.');
      return;
    }

    if (password != passwordcheck) {
      showError('Password must match');
      return;
    }

    if (password.length < 6) {
      showError('Password must be greater than six characters');
      return;
    }

    if (!validatePassword(password)) {
      showError('Password must contain both a capital letter, special character, and number');
      return;
    }

    try {
      const res = await axios.post('/api/register', {
        email: email,
        username: username, // or just use email directly if that's the field name
        password: password,
        timezone,
      });
      console.log('response status:', res.status);
      console.log('response data:', res.data);
      const data = res.data;
      if (data.success) {
        showSuccess(`Registration successful moving to email authentication...`);
        await delay(1000);
        if (onRegister) {
          console.log(data.email);
          onRegister({
            data,
          });
        }
      }
    } catch (err) {
      console.error('Registration error:', err);
      showError(err.response.data.message);
    }
  };
  return (
    <div className="auth-page">
      <div className="auth-layout">
        <aside className="auth-hero">
          <div>
            <p className="auth-hero__eyebrow">Task board</p>
            <h1 className="auth-hero__title">Create your account</h1>
            <p className="auth-hero__copy">
              Start with your timezone so digests arrive when you actually want them.
            </p>
          </div>

          <div className="auth-hero__notes">
            <span className="auth-hero__note">Timezone-aware digests</span>
            <span className="auth-hero__note">Recurring todos</span>
            <span className="auth-hero__note">Light and dark themes</span>
          </div>
        </aside>

        <section className="auth-card" style={{ animation: 'slideUp 0.6s ease-out' }}>
          <h2 className="auth-card__title">Register</h2>
          <p className="auth-card__copy">Set up your workspace, then jump straight into tasks.</p>

          {error && (
            <div className="settings-status" style={{ color: '#b42318' }}>
              {error}
            </div>
          )}
          {success && (
            <div className="settings-status" style={{ color: 'var(--brand)' }}>
              {success}
            </div>
          )}

          <form
            className="auth-form"
            onSubmit={(e) => {
              e.preventDefault();
              registeraccount();
            }}
          >
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input
                className="auth-input"
                type="text"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your email"
              />
            </div>

            <div className="auth-field">
              <label className="auth-label">Timezone</label>
              <Select
                value={timeZoneOptions.find((option) => option.value === timezone) || null}
                onChange={(option) => setTimezone(option?.value || detectTimeZone())}
                options={timeZoneOptions}
                styles={selectStyles}
                placeholder="Select your timezone"
                isSearchable={true}
              />
            </div>

            <div className="auth-field">
              <label className="auth-label">Username</label>
              <input
                className="auth-input"
                type="text"
                name="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your username"
              />
            </div>

            <div className="auth-field">
              <label className="auth-label">Password</label>
              <div className="auth-input-wrap">
                <input
                  className="auth-input"
                  type={isPasswordVisible ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setIsPasswordVisible((v) => !v)}
                  className="auth-toggle"
                >
                  {isPasswordVisible ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">Enter Password Again</label>
              <div className="auth-input-wrap">
                <input
                  className="auth-input"
                  type={isPasswordVisible ? 'text' : 'password'}
                  value={passwordcheck}
                  onChange={(e) => setPasswordCheck(e.target.value)}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setIsPasswordVisible((v) => !v)}
                  className="auth-toggle"
                >
                  {isPasswordVisible ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="auth-actions">
              <button type="button" onClick={registeraccount} className="auth-submit">
                Register
              </button>
            </div>

            <div className="auth-divider">or</div>

            <div className="auth-footer">
              <div className="text-center text-sm" style={{ color: 'var(--panel-muted)' }}>
                <Link to="/login" className="auth-link">
                  <span>&larr;</span> Return to login here
                </Link>
              </div>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
